import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './StrategyPanel.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * Convert a subset of Markdown to an HTML string.
 * Handles: ## headings, **bold**, *italic*, - bullets, ---, blank-line paragraphs.
 * HTML-escapes source text before applying inline transformations to prevent XSS.
 */
function markdownToHtml(text) {
  if (!text) return '';

  const esc = (s) => s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  const inline = (s) => esc(s)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');

  const lines = text.split('\n');
  const out = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) { i++; continue; }

    if (/^### /.test(line)) {
      out.push(`<h3>${inline(line.slice(4))}</h3>`);
      i++;
    } else if (/^## /.test(line)) {
      out.push(`<h2>${inline(line.slice(3))}</h2>`);
      i++;
    } else if (/^# /.test(line)) {
      out.push(`<h1>${inline(line.slice(2))}</h1>`);
      i++;
    } else if (/^---+$/.test(trimmed)) {
      out.push('<hr>');
      i++;
    } else if (/^[-*] /.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*] /.test(lines[i])) {
        items.push(`<li>${inline(lines[i].slice(2))}</li>`);
        i++;
      }
      out.push(`<ul>${items.join('')}</ul>`);
    } else {
      // Paragraph: collect consecutive non-special lines
      const paraLines = [];
      while (i < lines.length) {
        const l = lines[i];
        const t = l.trim();
        if (!t || /^#{1,3} /.test(l) || /^---+$/.test(t) || /^[-*] /.test(l)) break;
        paraLines.push(l.trim());
        i++;
      }
      out.push(`<p>${inline(paraLines.join(' '))}</p>`);
    }
  }

  return out.join('\n');
}

/** Shared pop-out HTML shell used by both inline and pop-out views. */
function buildPopOutHtml(bodyHtml) {
  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Strategy Document</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'DIN 2014',system-ui,sans-serif;background:#faf8f5;color:#3a3630;padding:2rem 3rem;max-width:820px;margin:0 auto;line-height:1.7}
  h1{font-size:1.4rem;font-weight:700;color:#1c566b;margin:1.5rem 0 .5rem}
  h2{font-size:1.1rem;font-weight:700;color:#1c566b;margin:1.25rem 0 .4rem;border-bottom:1px solid #e0d8cc;padding-bottom:.25rem}
  h3{font-size:.95rem;font-weight:700;color:#2a4a58;margin:1rem 0 .3rem}
  p{font-size:.87rem;color:#555;margin-bottom:.6rem}
  ul{margin:.3rem 0 .8rem 1.4rem}
  li{font-size:.87rem;color:#555;margin-bottom:.3rem;list-style:disc}
  hr{border:none;border-top:1px solid #e0d8cc;margin:1.2rem 0}
  strong{color:#333;font-weight:600}
  em{font-style:italic;color:#555}
  .footer{margin-top:2rem;font-size:.65rem;color:#aaa;text-align:center;border-top:1px solid #e0d8cc;padding-top:.75rem}
</style></head><body>
${bodyHtml}
<div class="footer">nexus — strategy document</div>
</body></html>`;
}

export default function StrategyPanel({ authFetch }) {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    authFetch(`${API_BASE_URL}/api/strategy_document`)
      .then(r => r.json())
      .then(data => {
        setContent(data.content || '');
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [authFetch]);

  const renderedHtml = useMemo(() => markdownToHtml(content), [content]);

  const handlePopOut = useCallback(() => {
    const w = window.open('', '_blank', 'width=860,height=900,scrollbars=yes');
    if (w) {
      w.document.write(buildPopOutHtml(renderedHtml));
      w.document.close();
    }
  }, [renderedHtml]);

  return (
    <div className="strategy-panel">
      <div className="strategy-panel-toolbar">
        <span className="strategy-panel-title">Strategy Document</span>
        {!loading && !error && content && (
          <button
            className="strategy-popout-btn"
            onClick={handlePopOut}
            title="Open in new window"
          >
            ⧉ <span className="strategy-popout-label">Pop out</span>
          </button>
        )}
      </div>

      <div className="strategy-panel-body">
        {loading && (
          <p className="strategy-status">Loading…</p>
        )}
        {error && (
          <p className="strategy-status strategy-error">Could not load document: {error}</p>
        )}
        {!loading && !error && !content && (
          <p className="strategy-status strategy-empty">
            No strategy document found.
            <br />
            Add a <code>strategy.md</code> file to the data folder to populate this tab.
          </p>
        )}
        {!loading && !error && content && (
          <div
            className="strategy-content"
            dangerouslySetInnerHTML={{ __html: renderedHtml }}
          />
        )}
      </div>
    </div>
  );
}
